import type { ExtendedCardData } from '../../utils/cardManager';

/**
 * カードプール設定
 */
export interface CardPoolConfig {
  /** 除外するカードIDのセット */
  excludedCardIds?: Set<string>;
  /** 許可するカードIDのセット（指定がなければ全て許可、指定があればこのセットのみ許可） */
  allowedCardIds?: Set<string>;
  /** 許可する属性（指定がなければ全て許可） */
  allowedAttributes?: string[];
  /** 最大コスト制限 */
  maxCost?: number;
  /** 禁忌カードを除外するか */
  excludeForbidden?: boolean;
}

/**
 * カード禁忌判定（SimNPC.tsからの移植を避けるためここで再定義）
 */
function isCardForbiddenLocal(card: { forbidden?: boolean }): boolean {
  return card.forbidden === true;
}

/**
 * 設定に基づいて利用可能なカードをフィルタリング
 * @param allCards 全カードリスト
 * @param config フィルタ設定
 * @returns フィルタリングされたカードリスト
 */
export function getAvailableCards(
  allCards: ExtendedCardData[],
  config?: CardPoolConfig
): ExtendedCardData[] {
  if (!config) {
    return allCards;
  }

  return allCards.filter((card) => {
    // 許可カードIDチェック（設定されている場合、このセットに含まれるカードのみ許可）
    if (config.allowedCardIds && config.allowedCardIds.size > 0) {
      if (!config.allowedCardIds.has(card.id)) {
        return false;
      }
    }

    // 除外カードIDチェック
    if (config.excludedCardIds?.has(card.id)) {
      return false;
    }

    // 属性制限チェック
    if (config.allowedAttributes && config.allowedAttributes.length > 0) {
      if (!config.allowedAttributes.includes(card.attribute)) {
        return false;
      }
    }

    // コスト制限チェック
    if (config.maxCost !== undefined && card.cost > config.maxCost) {
      return false;
    }

    // 禁忌カード除外チェック
    if (config.excludeForbidden && isCardForbiddenLocal(card)) {
      return false;
    }

    return true;
  });
}

/**
 * 利用可能なカードからランダムに1枚選択
 * @param allCards 全カードリスト
 * @param config フィルタ設定
 * @returns ランダムに選ばれたカード（なければnull）
 */
export function getRandomAvailableCard(
  allCards: ExtendedCardData[],
  config?: CardPoolConfig
): ExtendedCardData | null {
  const available = getAvailableCards(allCards, config);
  if (available.length === 0) {
    return null;
  }
  return available[Math.floor(Math.random() * available.length)];
}

/**
 * 属性でフィルタリングした利用可能なカードを取得
 * @param allCards 全カードリスト
 * @param attribute 属性
 * @param config フィルタ設定
 * @returns フィルタリングされたカードリスト
 */
export function getAvailableCardsByAttribute(
  allCards: ExtendedCardData[],
  attribute: string,
  config?: CardPoolConfig
): ExtendedCardData[] {
  const available = getAvailableCards(allCards, config);
  return available.filter((card) => card.attribute === attribute);
}

/**
 * タイプでフィルタリングした利用可能なカードを取得
 * @param allCards 全カードリスト
 * @param cardType カードタイプ（monster, magic, field）
 * @param config フィルタ設定
 * @returns フィルタリングされたカードリスト
 */
export function getAvailableCardsByType(
  allCards: ExtendedCardData[],
  cardType: string,
  config?: CardPoolConfig
): ExtendedCardData[] {
  const available = getAvailableCards(allCards, config);
  return available.filter((card) => card.type === cardType);
}
